import { Event } from '@shared/core/events'
import { PreferencesDateFieldOrder } from '@shared/core/preferences'
import { GetProfileResponseDto } from '@shared/core/users'
import { addMinutes, format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import type { RRuleSet } from 'rrule'
import { isMissingOrEmpty } from 'src/helpers/is-missing'
import type { EventValidationResponse } from 'src/modules/event/types/event.types'
import { dateOrders } from 'src/modules/user/constants/date-orders.constants'
import { DateOrders } from 'src/modules/user/types/date-orders.types'

export function formatEventDate(
  eventDate: Date | string,
  dateOrder: keyof DateOrders | PreferencesDateFieldOrder,
  timezone?: string,
) {
  const dt = eventDate instanceof Date ? eventDate : new Date(Number(eventDate))
  return timezone
    ? formatInTimeZone(dt, timezone, dateOrders[dateOrder] || dateOrders.MDY)
    : format(dt, dateOrders[dateOrder] || dateOrders.MDY)
}

export function stripHtmlTags(input: string): string {
  const doc = new DOMParser().parseFromString(input ?? '', 'text/html')
  return doc.body.textContent
}

export function getExpiredConfirmationOffsets(eventDate: Date, offsets: number[]): number[] {
  const now = new Date()
  return (offsets ?? []).filter((offset) => now.getTime() + offset > eventDate.getTime())
}

export function getExpiredReminderOffsets(eventDate: Date, offsets: number[]): number[] {
  const now = new Date()
  const msBetweenDates = Math.abs(eventDate.getTime() - now.getTime())
  return (offsets ?? []).filter((t) => msBetweenDates < t)
}

export function updateRruleExdates(
  dtstart: Date,
  rruleObject: RRuleSet,
  addTZDifferenceWithUTC?: boolean,
  offset?: number,
): Date[] {
  const dtstartHours = dtstart.getUTCHours()
  const dtstartMinutes = dtstart.getUTCMinutes()
  const dtstartSeconds = dtstart.getUTCSeconds()

  return rruleObject._exdate.map((date) => {
    let updatedDate = new Date(date)

    if (addTZDifferenceWithUTC && typeof offset === 'number') {
      // add time zone difference with UTC to date
      updatedDate = addMinutes(date, -offset)
    }

    updatedDate.setUTCHours(dtstartHours)
    updatedDate.setUTCMinutes(dtstartMinutes)
    updatedDate.setUTCSeconds(dtstartSeconds)

    return updatedDate
  })
}

export function validateEvent(event: Event, profile: GetProfileResponseDto): EventValidationResponse {
  const isNumberError = isMissingOrEmpty(event.receiver_numbers)

  const isMessageError =
    isMissingOrEmpty(event.confirmation_message) ||
    isMissingOrEmpty(profile.default_message_template.confirmation_message)

  const isTimingError =
    isMissingOrEmpty(event.reminders.data) || isMissingOrEmpty(profile.default_message_template.confirmation_offsets)

  return { isNumberError, isMessageError, isTimingError }
}
